import type {
  LiveState,
  MessageState,
  PauseState,
  WorkshopId,
} from "../../../api/workshop";
import MiniEvent from "../../../lib/mini-event";

// What components read via `useLiveState`: server truth folded together with the
// derived UI state below. The pause is the server's raw state — whether the
// teacher's dialog is open is plain local UI state in the toolbar's ClassroomMenu,
// not something this store tracks. The message is the client-latched one (see
// `latchedMessage`), not the server's — it outlives the server's short replay
// window and hides only when this client dismisses it.
export type LiveView = {
  pause: PauseState | null;
  message: MessageState | null;
};

const EMPTY_VIEW: LiveView = { pause: null, message: null };

// The state before (or after) a connection is open. A shared constant so
// `getState` returns a referentially stable value — `useSyncExternalStore` only
// skips a re-render when the snapshot identity is unchanged.
export type LiveStore = {
  getState: () => LiveView;
  subscribe: (listener: () => void) => () => void;
};

type WorkshopEntry = {
  workshopId: WorkshopId;
  // Last state received from the server — the SSE source of truth.
  server: LiveState;
  // Cached combined view handed to `useSyncExternalStore`. Rebuilt only when
  // something actually changes, so its identity is stable between changes.
  view: LiveView;
  event: MiniEvent;
  source: EventSource;
  refs: number;
};

const registry = new Map<WorkshopId, WorkshopEntry>();

// The message this client is currently showing, per workshop. A message is an
// event, not state: the server keeps its copy only for a short replay window
// (~30s, so reconnecting/late-joining students still receive it) and then drops
// it — but once a student has been shown a message we keep it up until they
// acknowledge it. So we latch the received message here and stop mirroring the
// server's clear (see `reconcile`/`buildView`); `dismissMessage` clears it.
// Kept OUTSIDE `WorkshopEntry` so it survives the SSE stream being torn down and
// reopened on reconnect/remount/navigation.
const latchedMessage = new Map<WorkshopId, MessageState>();

// The `issuedAt` of the message this client has dismissed, per workshop. Guards
// against re-latching the same message when the server replays it over its
// window (on reconnect/remount/navigation) — without it, a message this client
// already acknowledged would pop the dialog back open. A genuinely new broadcast
// carries a new `issuedAt`, so it no longer matches here and shows as intended.
// Kept OUTSIDE `WorkshopEntry` for the same longevity reason as `latchedMessage`.
const dismissedMessageAt = new Map<WorkshopId, number>();

const buildView = (entry: WorkshopEntry): LiveView => ({
  pause: entry.server.pause,
  // The latched message, not the server's — it outlives the server's replay
  // window and hides only once this client dismisses it (see `latchedMessage`).
  message: latchedMessage.get(entry.workshopId) ?? null,
});

/**
 * Fold a freshly received server state into `entry`. A newly issued message is
 * latched so it stays up until this client dismisses it, independent of the
 * server's short-lived copy; a message the client already dismissed isn't
 * re-latched when the server replays it over its window (see `latchedMessage`
 * and `dismissedMessageAt`).
 */
const reconcile = (entry: WorkshopEntry, next: LiveState) => {
  const incoming = next.message;
  if (
    incoming &&
    dismissedMessageAt.get(entry.workshopId) !== incoming.issuedAt &&
    latchedMessage.get(entry.workshopId)?.issuedAt !== incoming.issuedAt
  ) {
    latchedMessage.set(entry.workshopId, incoming);
  }
  // Deliberately no `else` clearing the latch: the server drops the message
  // after its replay window, but the student keeps seeing it until they dismiss.

  entry.server = next;
  entry.view = buildView(entry);
};

/** Open the SSE stream and start mirroring its `state` frames into `entry`. */
const openEntry = (workshopId: WorkshopId): WorkshopEntry => {
  const source = new EventSource(
    `${import.meta.env.VITE_API_HOST}/workshops/${workshopId}/sse`,
    // The API authenticates the stream by cookie; EventSource only sends
    // credentials cross-origin when this is set (matches `credentials: include`
    // used everywhere else).
    { withCredentials: true },
  );

  const entry: WorkshopEntry = {
    workshopId,
    server: { pause: null, message: null },
    view: EMPTY_VIEW,
    event: new MiniEvent(),
    source,
    refs: 0,
  };

  // The server emits the full LiveState as a named `state` event on connect and
  // on every change, so we always replace — never merge.
  source.addEventListener("state", (e) => {
    let next: LiveState;
    try {
      next = JSON.parse(e.data) as LiveState;
    } catch {
      return; // ignore a malformed frame rather than tearing down the stream
    }
    reconcile(entry, next);
    entry.event.emit();
  });

  // EventSource reconnects on its own after a transient error, so there's
  // nothing to do here but keep serving the last-known state until it recovers.

  return entry;
};

const teardownEntry = (workshopId: WorkshopId, entry: WorkshopEntry) => {
  entry.source.close();
  registry.delete(workshopId);
};

/**
 * Get the shared live-state store for a workshop. The SSE connection opens on
 * the first subscriber and closes when the last one unsubscribes.
 */
export const getLiveStore = (workshopId: WorkshopId): LiveStore => ({
  getState: () => registry.get(workshopId)?.view ?? EMPTY_VIEW,
  subscribe: (listener) => {
    let entry = registry.get(workshopId);
    if (!entry) {
      entry = openEntry(workshopId);
      registry.set(workshopId, entry);
    }
    const remove = entry.event.addListener(listener);
    entry.refs += 1;

    return () => {
      remove();
      entry.refs -= 1;
      if (entry.refs === 0) teardownEntry(workshopId, entry);
    };
  },
});

/**
 * Dismiss the current message for this client ("Verstanden"). Drops the latch so
 * it hides, and records its `issuedAt` so a server replay within the window
 * doesn't pop it back up.
 */
export const dismissMessage = (workshopId: WorkshopId) => {
  const latched = latchedMessage.get(workshopId);
  if (!latched) return;

  latchedMessage.delete(workshopId);
  dismissedMessageAt.set(workshopId, latched.issuedAt);

  const entry = registry.get(workshopId);
  if (entry) {
    entry.view = buildView(entry);
    entry.event.emit();
  }
};
